/*
 * Copyright (c) 2008-2025 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
 * This is CONFIDENTIAL code. Use is subject to license terms.
 */

import api from "@/config/api"
import Vue from "vue"

/* global log */

export default {
  namespaced: true,

  state: {
    // Settings coming from deployed system's properties
    systemConfiguration: {
      status: null,
      loadPromise: null,
      config: {},
    },
    maintenanceMessage: {
      status: null,
      message: {},
      dismissed: false,
    },
  },

  mutations: {
    /**
     * commit loaded config to state
     * @param state
     * @param config
     */
    loadSystemConfiguration(state, config) {
      state.systemConfiguration.config = config
      state.systemConfiguration.status = "loaded"
    },

    /**
     * commit the promise for loading config to state
     * @param state
     * @param loadPromise
     */
    loadPromise(state, loadPromise) {
      state.loadPromise = loadPromise // TODO: Rather state.systemConfiguration.loadPromise?
    },

    /**
     * commit loaded maintenance message to state
     * @param state
     * @param message
     */
    loadMaintenanceMessage(state, message) {
      state.maintenanceMessage.message = message
      state.maintenanceMessage.status = "loaded"
    },

    /**
     * commit dismissed maintenance message to state
     * @param state
     */
    dismissMaintenanceMessage(state) {
      state.maintenanceMessage.dismissed = true
    },
  },

  actions: {
    /**
     * load config from server and return promise (empty) when complete.
     *
     * Hint: Only a single load request will be triggered. Multiple dispatches won't result in multiple requests and
     * can thus not be used for refreshing configuration data.
     *
     * @param state
     * @param commit
     * @return promise that resolves (empty) when the configuration finished loading.
     */
    loadSystemConfiguration({ state, commit }) {
      // TODO: Rather state.systemConfiguration.loadPromise?
      if (!state.loadPromise) {
        let promise = Vue.http.get(api.SYSTEM_CONFIGURATION())
        commit(
          "loadPromise",
          promise.then(() => {})
        ) // make the promise return empty and force the use of getters
        promise.then(
          (response) => commit("loadSystemConfiguration", response.body),
          () => log.error("Error loading system configuration. Control center might behave unexpectedly.")
        )
      }
      return state.loadPromise // TODO: Rather state.systemConfiguration.loadPromise?
    },

    /**
     * load maintenance message from server
     *
     * @param commit
     */
    loadMaintenanceMessage({ commit }) {
      Vue.http.get(api.MAINTENANCE_MESSAGE()).then(
        (response) => commit("loadMaintenanceMessage", response.body),
        () => log.error("Error loading maintenance message.")
      )
    },

    /**
     * update maintenance message on server
     *
     * @param data
     * @returns {Promise<any>}
     */
    updateMaintenanceMessage({ commit }, { data }) {
      return new Promise((resolve, reject) => {
        Vue.http.put(api.MAINTENANCE_MESSAGE(), data).then(
          (response) => {
            commit("loadMaintenanceMessage", response.body)
            resolve(response.data)
          },
          (response) => {
            reject(response)
          }
        )
      })
    },
  },

  getters: {
    /**
     * get whether system configuration has loaded
     * @param state
     * @returns {boolean}
     */
    systemConfigurationLoaded: (state) => state.systemConfiguration.status === "loaded",

    /**
     * get system configuration item
     * @param state
     * @returns {function(*): *}
     */
    getSystemConfiguration: (state) => (key) => state.systemConfiguration.config[key],

    /**
     * get whether the SaaS mode is enabled
     * (we only return true once the configuration has been loaded in order to avoid UI flickering)
     * @param state
     */
    isSaasModeEnabled: (state) => {
      return (
        state.systemConfiguration.status === "loaded" && state.systemConfiguration.config["saasModeEnabled"] === "true"
      )
    },

    /**
     * get whether the SaaS mode is disabled
     * (we only return true once the configuration has been loaded in order to avoid UI flickering)
     * @param state
     */
    isSaasModeDisabled: (state) => {
      return (
        state.systemConfiguration.status === "loaded" && state.systemConfiguration.config["saasModeEnabled"] === "false"
      )
    },

    /**
     * Gets whether the tenant license mechanism is enabled or not
     *
     * @param state
     * @return {boolean}
     */
    isLicenseMechanismEnabled: (state) => {
      return (
        state.systemConfiguration.status === "loaded" &&
        state.systemConfiguration.config["isLicenseMechanismEnabled"] === "true"
      )
    },

    /**
     * get to simplify the boolean logic of whether the email service has been configured on the server
     *
     * @param state
     */
    isEmailServerConfigured: (state) => {
      return (
        state.systemConfiguration.status === "loaded" &&
        state.systemConfiguration.config["isEmailServiceAvailable"] === "true"
      )
    },

    isStreamingServerAvailable: (state) => {
      return (
        state.systemConfiguration.status === "loaded" &&
        state.systemConfiguration.config["isStreamingServerAvailable"] === "true"
      )
    },

    isZoomAvailable: (state) => {
      return (
        state.systemConfiguration.status === "loaded" && state.systemConfiguration.config["isZoomAvailable"] === "true"
      )
    },

    isEventMaterialConfigured: (state) => {
      return (
        state.systemConfiguration.status === "loaded" && state.systemConfiguration.config["hasEventMaterial"] === "true"
      )
    },

    electionsEnabled: (state) => {
      return (
        state.systemConfiguration.status === "loaded" && state.systemConfiguration.config["electionsEnabled"] === "true"
      )
    },

    autojoinMeetingEnabled: (state) => {
      return (
        state.systemConfiguration.status === "loaded" &&
        state.systemConfiguration.config["autojoinMeetingEnabled"] === "true"
      )
    },

    /**
     * Get the list of user categories available to a meeting
     *
     * @returns {Array}
     */
    userCategories: (state) => () => {
      const categoriesString =
        state.systemConfiguration.status === "loaded" &&
        state.systemConfiguration.config.userCategories &&
        (typeof state.systemConfiguration.config.userCategories === "string" ||
          state.systemConfiguration.config.userCategories instanceof String)
          ? state.systemConfiguration.config.userCategories.trim()
          : "[]"
      return JSON.parse(categoriesString)
    },

    /**
     * Returns whether the waiting room feature has been enabled on the backend.
     *
     * @param state
     * @return {boolean}
     */
    isWaitingRoomSupported: (state) => {
      return (
        state.systemConfiguration.status === "loaded" &&
        state.systemConfiguration.config["waitingRoomSupport"] === "true"
      )
    },

    /**
     * Returns whether the meeting quorum is enabled on the backend
     */
    isMeetingQuorumEnabled: (state) => {
      return (
        state.systemConfiguration.status === "loaded" &&
        state.systemConfiguration.config["meetingQuorumEnabled"] === "true"
      )
    },

    /**
     * Returns list of excluded custom meeting properties
     */
    excludedCustomMeetingProperties: (state) => {
      return (
        (state.systemConfiguration.status === "loaded" &&
          state.systemConfiguration.config["excludedCustomMeetingProperties"]) ||
        []
      )
    },

    /**
     * Returns an object with the participantImport defaults (config properties starting with "participantImport.")
     */
    getParticipantImportDefaults: (state) => {
      return Object.entries(state.systemConfiguration.config)
        .filter(([key]) => key.startsWith("participantImport."))
        .reduce((obj, [key, value]) => {
          obj[key.slice("participantImport.".length)] = value
          return obj
        }, {})
    },

    /**
     * Returns the maintenance message
     * @param state
     * @returns {Object}
     */
    getMaintenanceMessage: (state) => {
      return state.maintenanceMessage.status === "loaded" && state.maintenanceMessage.message
    },

    /**
     * Returns whether the maintenance message has been dismissed by the user
     * @param state
     * @returns {Boolean}
     */
    isMaintenanceMessageDismissed: (state) => {
      return state.maintenanceMessage.dismissed
    },

    defaultTenantAccreditationText: (state) => {
      if (state.systemConfiguration.status === "loaded") {
        return state.systemConfiguration?.config["defaultTenantAccreditationText"]
      } else {
        return ""
      }
    },
  },
}
